import { useCallback, useEffect, useRef, useState } from 'react';
import { getListProvider } from '@/services/api/providers';
import { getOutletsByProvider } from '@/services/api/outlets';
import { getListDrivers } from '@/services/api/drivers';

export interface DropdownOption {
  value: string;
  label: string;
}

interface UseDropdownResult {
  options: DropdownOption[];
  loading: boolean;
  error: string | null;
  setSearch: (value: string) => void;
  /** Look up a label for a previously-selected value, even if the current
   *  search has narrowed it out of the visible result set. */
  labelFor: (value: string) => string;
}

// Shared shape between the three hooks below: open-gate, debounced search,
// label cache so the dropdown still renders the selected option's name
// even after a new search trims it out of the results.
function useDropdownLoader(
  open: boolean,
  fetcher: (search: string) => Promise<DropdownOption[]>,
  /** Extra dependency that should re-fire the initial fetch — used by the
   *  outlet hook to reload when the selected client changes. */
  reloadKey: string | number | null = null
): UseDropdownResult {
  const [options, setOptions] = useState<DropdownOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const labelCache = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (!open) {
      setSearch('');
      return;
    }

    const trimmed = search.trim();
    let cancelled = false;

    const fire = () => {
      setLoading(true);
      fetcher(trimmed)
        .then((items) => {
          if (cancelled) return;
          setOptions(items);
          setError(null);
          for (const it of items) labelCache.current.set(it.value, it.label);
        })
        .catch((e) => {
          if (cancelled) return;
          setError(e instanceof Error ? e.message : 'Failed to load options');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };

    let timer: ReturnType<typeof setTimeout> | undefined;
    if (trimmed) {
      timer = setTimeout(fire, 500);
    } else {
      fire();
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // reloadKey is intentionally a dep — see comment on the prop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, search, reloadKey]);

  const labelFor = useCallback(
    (value: string) => labelCache.current.get(value) ?? '',
    []
  );

  return { options, loading, error, setSearch, labelFor };
}

export function useClientOptions(open: boolean): UseDropdownResult {
  return useDropdownLoader(open, async (search) => {
    const res = await getListProvider({
      page: '1',
      size: '9999',
      ...(search ? { search } : {}),
    });
    return res.data.map((p) => ({ value: String(p.id), label: p.name }));
  });
}

export function useOutletOptions(
  open: boolean,
  providerID: string | null
): UseDropdownResult {
  return useDropdownLoader(
    open && Boolean(providerID),
    async (search) => {
      if (!providerID) return [];
      const res = await getOutletsByProvider(providerID, {
        page: 1,
        size: 9999,
        ...(search ? { search } : {}),
      });
      return res.data.map((o) => ({ value: String(o.id), label: o.name }));
    },
    providerID
  );
}

export function useDriverOptions(open: boolean): UseDropdownResult {
  return useDropdownLoader(open, async (search) => {
    const res = await getListDrivers({
      page: '1',
      size: '50',
      sort: 'created_at',
      order: 'desc',
      active: 'true',
      search: search || '',
    });
    return res.data.map((d) => {
      const fullName =
        [d.first_name, d.last_name].filter(Boolean).join(' ') || d.name;
      const code = d.code || d.phone || '';
      return {
        value: String(d.id),
        label: code ? `${fullName} (${code})` : fullName,
      };
    });
  });
}
